import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ICategory, IErrorResponse, IFilter, ISegment, ISegmentData, IStudyState, ISubSegment } from "../models/interfaces";

const initialState: ISegmentData = {
	basicSegmentDetails: { id: "", name: "", description: "" },
	createSegmentDetails: {
		study_id: "",
		segment: {
			name: "",
			description: "",
			category: [],
			sub_seg: [], // Array of sub-segments inside the segment
		},
	},
	activeSubSegment: null,
	categoryDetails: [],
	originalCategoryDetails: [],
	listOfSegments: [],
	isCreateSegment: false,
	loadingSpin: false,
	btnLoading: false,
	btnDisabled: true,
	isEditable: false,
	isSegmentLoading: false,
	hasError: false,
	errorResponse: [],
	isReapplySegment: false,
	isReapplyBtnEnabled: false,
	isReapplyModalOpen: false,
};

// 🛠 Corrected Reducer
const quantSegmentSlice = createSlice({
	name: "quantSegmentData",
	initialState: initialState,
	reducers: {
		// Set basic segment details
		setBasicSegmentDetails: (state, action: PayloadAction<ISegmentData["basicSegmentDetails"]>) => {
			state.basicSegmentDetails = action.payload;
		},
		updateSegmentDetails: (state, action: PayloadAction<Partial<Pick<ISegment, "name" | "description">>>) => {
			const { name, description } = action.payload;
			if (name) state.createSegmentDetails.segment.name = name;
			if (description) state.createSegmentDetails.segment.description = description;
		},
		addSubSegment: (state, action: PayloadAction<ISubSegment>) => {
			state.createSegmentDetails.segment.sub_seg.push(action.payload);
		},
		deleteSubSegment: (state, action: PayloadAction<string>) => {
			// Filter out the deleted sub-segment
			const updatedSubSegments = state.createSegmentDetails.segment.sub_seg.filter((sub) => sub.name !== action.payload);
			state.createSegmentDetails.segment.sub_seg = updatedSubSegments;
		},
		updateSubSegmentName: (state, action: PayloadAction<{ name: string; _id: number }>) => {
			const index = state.createSegmentDetails.segment.sub_seg.findIndex((sub) => sub._id === action.payload._id);
			if (index !== -1) {
				state.createSegmentDetails.segment.sub_seg[index].name = action.payload.name;
			}
		},
		updateAllSubSegments: (state, action: PayloadAction<ISubSegment[]>) => {
			state.createSegmentDetails.segment.sub_seg = action.payload;
		},
		updateSubSegmentEditable: (state, action: PayloadAction<{ isEditable: boolean; _id: number }>) => {
			const index = state.createSegmentDetails.segment.sub_seg.findIndex((sub) => sub._id === action.payload._id);
			if (index !== -1) {
				state.createSegmentDetails.segment.sub_seg[index].isEditable = action.payload.isEditable;
			}
		},
		updateSubSegment: (state, action: PayloadAction<ISubSegment>) => {
			const index = state.createSegmentDetails.segment.sub_seg.findIndex((sub) => sub.name === action.payload.name);
			if (index !== -1) {
				state.createSegmentDetails.segment.sub_seg[index] = action.payload;
			}
		},
		updateSubSegmentFilters: (state, action: PayloadAction<{ _id: number; filters: IFilter[] }>) => {
			const { _id, filters } = action.payload;
			const index = state.createSegmentDetails.segment.sub_seg.findIndex((sub) => sub._id === _id);
			if (index !== -1) {
				state.createSegmentDetails.segment.sub_seg[index].filters = filters;
				// state.createSegmentDetails.segment.sub_seg[index].dimensions = dimensions;
				state.activeSubSegment = state.createSegmentDetails.segment.sub_seg[index];
			}
		},
		updateNSizeAndDimensions: (state, action: PayloadAction<{ updates: { name: string; n_size: number; dimensions: number }[] }>) => {
			const { updates } = action.payload;

			updates.forEach(({ name, n_size, dimensions }) => {
				const subSegment = state.createSegmentDetails.segment.sub_seg.find((sub) => sub.name === name);
				if (subSegment) {
					subSegment.n_size = n_size;
					subSegment.dimensions = dimensions;
					subSegment.loadingSpin = false;
				}
			});
		},
		setActiveSubSegment: (state, action: PayloadAction<ISubSegment | null>) => {
			state.activeSubSegment = action.payload;
		},
		setCategoryDetails: (state, action: PayloadAction<ICategory[]>) => {
			state.categoryDetails = action.payload;
			state.originalCategoryDetails = action.payload;
		},
		setVisibleCreateSegment: (state, action: PayloadAction<boolean>) => {
			state.isCreateSegment = action.payload;
		},
		setListOfSegment: (state, action: PayloadAction<ISegment[]>) => {
			state.listOfSegments = action.payload;
		},
		updateCategoryDetails: (state, action: PayloadAction<ICategory[]>) => {
			state.categoryDetails = action.payload;
		},
		updateLoadingSpin: (state, action: PayloadAction<boolean>) => {
			state.loadingSpin = action.payload;
		},
		updateBtnLoading: (state, action: PayloadAction<boolean>) => {
			state.btnLoading = action.payload;
		},
		updateSegmentData: (state, action: PayloadAction<IStudyState>) => {
			state.createSegmentDetails = action.payload;
		},
		resetAllData: (state) => {
			state.createSegmentDetails = {
				study_id: "",
				segment: {
					name: "",
					description: "",
					category: [],
					sub_seg: [], // Array of sub-segments inside the segment
				},
			};
			state.activeSubSegment = null;
		},
		updateIsEditable: (state, action: PayloadAction<boolean>) => {
			state.isEditable = action.payload;
		},
		updateBtnDisabled: (state, action: PayloadAction<boolean>) => {
			state.btnDisabled = action.payload;
		},
		updateSegmentLoading: (state, action: PayloadAction<boolean>) => {
			state.isSegmentLoading = action.payload;
		},
		updateSegmentError: (state, action: PayloadAction<boolean>) => {
			state.hasError = action.payload;
		},
		updateResponseErrors: (state, action: PayloadAction<IErrorResponse[]>) => {
			state.errorResponse = action.payload;
		},
		setIsReapplySegment(state, action: PayloadAction<boolean>) {
			state.isReapplySegment = action.payload;
		},
		setIsReapplyBtnEnabled(state, action: PayloadAction<boolean>) {
			state.isReapplyBtnEnabled = action.payload;
		},
		setReapplyModalOpen(state, action: PayloadAction<boolean>) {
			state.isReapplyModalOpen = action.payload;
		},
	},
});

export const {
	setBasicSegmentDetails,
	updateSegmentDetails,
	updateSubSegmentEditable,
	updateSubSegmentName,
	addSubSegment,
	deleteSubSegment,
	updateSubSegment,
	setActiveSubSegment,
	updateSubSegmentFilters,
	setCategoryDetails,
	updateNSizeAndDimensions,
	setVisibleCreateSegment,
	setListOfSegment,
	updateCategoryDetails,
	updateLoadingSpin,
	updateBtnLoading,
	resetAllData,
	updateSegmentData,
	updateAllSubSegments,
	updateIsEditable,
	updateResponseErrors,
	updateBtnDisabled,
	updateSegmentLoading,
	updateSegmentError,
	setIsReapplySegment,
	setIsReapplyBtnEnabled,
	setReapplyModalOpen,
} = quantSegmentSlice.actions;

export default quantSegmentSlice.reducer;
